import { Box, Divider, Grid2, Typography } from '@mui/material'
import { ReviewPaymentDataFee } from 'api/payments/types'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { dashboardPath } from 'components/Routes'
import { voucherPath } from 'components/Routes/VoucherFlowRoutes'
import DefaultLayout from 'components/layout/DefaultLayout'
import TransactionButtons from 'components/TransactionButtons/TransactionButtons'
import { useWallet } from 'hooks/useWallet'
import { useMutateBuyVoucher } from 'hooks/useVouchers'
import { formattedPrice, roundUpDollarsAndCents } from 'utils/util'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import useStyles from './styles'

export default function VoucherReview(): JSX.Element {
  const { classes } = useStyles()

  const navigate = useNavigate()
  const { setToast, user } = useAuth()
  const [disabledCCButton, setDisabledCCButton] = useState<boolean>(true)
  const [reviewPaymentDataFee, setReviewPaymentDataFee] =
    useState<ReviewPaymentDataFee | null>(null)
  const { state } = useLocation()

  const { voucher_amount, toEmail, toName, note } = state || {}

  const { data: walletData, isLoading: walletLoading, refetch } = useWallet()

  const walletCredit = Number(walletData?.walletBalance || 0)

  const { mutate: buyVoucher, isLoading: useBuyVoucherLoading } =
    useMutateBuyVoucher()

  const loading = useBuyVoucherLoading || walletLoading

  const handleBuyVoucher = () => {
    buyVoucher(
      {
        amount: Number(voucher_amount),
        from_name: user?.profile?.firstName ?? '',
        from_email: user?.email ?? '',
        to_name: toName,
        to_email: toEmail,
        note
      },
      {
        onSuccess: () => {
          setToast({
            message: `Your gift for ${toName} it on its way 💝`,
            open: true,
            duration: 3000,
            type: 'success'
          })
          navigate(dashboardPath())
        },
        onError: (error: unknown) =>
          setToast({
            message:
              error instanceof Error ? error.message : 'An error occurred',
            open: true,
            duration: 3000,
            type: 'error'
          })
      }
    )
  }

  useEffect(() => {
    if (!state) {
      navigate(voucherPath())
    }
  }, [navigate, state])

  const handleCCConfirm = useCallback(async () => {
    if (!disabledCCButton) {
      const paymentForm = document.getElementById(
        'credit-or-debit-form'
      ) as HTMLFormElement
      if (paymentForm) {
        paymentForm.requestSubmit()
      }
    }
  }, [disabledCCButton])

  const handleCreatePaymentSuccess = (
    reviewPaymentDataFee: ReviewPaymentDataFee
  ) => {
    setReviewPaymentDataFee({
      payment_amount: reviewPaymentDataFee.payment_amount,
      fee: reviewPaymentDataFee.fee
    })
  }

  const amount = Number(voucher_amount)

  const netWalletBalance = walletCredit >= amount ? amount : walletCredit

  // Either we have enough in our wallet so our wallet pays the full balance
  // XOR we don't have enough, so we use it all XOR we're negative credit so
  // we're using zero from our wallet
  const totalWalletBalanceToUse =
    walletCredit >= amount ? amount : Math.max(walletCredit, 0)
  // Rounds up to ensure that we don't miss a fraction of a cent and then prevent purchase.
  const totalTopUp = roundUpDollarsAndCents(amount - netWalletBalance)

  return (
    <DefaultLayout
      wrapperContainer={{ px: 2, py: 4 }}
      wrapperContent={{ m: 0 }}
      heading={'Review Gift'}
      showBackIcon
    >
      <Grid2 p={2} className={classes.root}>
        <Grid2 container>
          <Typography component='h2' textAlign='center'>
            {`We will email ${toName} your message and gift voucher to ${toEmail}`}
          </Typography>
        </Grid2>

        <Divider className={classes.divider} />

        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          my={2}
        >
          <Box>
            <Typography textTransform='uppercase'>Wallet</Typography>
            <Typography variant='body2'>
              Use ${Number(totalWalletBalanceToUse).toFixed(2)}
            </Typography>
          </Box>
          <Typography>${netWalletBalance.toFixed(2)}</Typography>
        </Box>

        {totalTopUp > 0 && (
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            my={2}
          >
            <Box>
              <Typography textTransform='uppercase'>Top up</Typography>
              <Typography variant='body2'>
                You'll need to add ${Number(totalTopUp).toFixed(2)}
              </Typography>
            </Box>
            <Typography>${totalTopUp.toFixed(2)}</Typography>
          </Box>
        )}

        <Divider />

        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          my={2}
        >
          <Box>
            <Typography textTransform='uppercase'>Total</Typography>
          </Box>
          <Typography>${amount.toFixed(2)}</Typography>
        </Box>

        <Divider className={classes.divider} />

        {totalTopUp > 0 && (
          <Grid2>
            <Typography>
              You don't have enough funds in your wallet to send this gift.
            </Typography>
            <TransactionButtons
              price={totalTopUp}
              setDisabledButton={setDisabledCCButton}
              loading={loading}
              paymentParams={{
                amount: totalTopUp.toString(),
                payment_type: 'top_up_to_wallet_for_voucher',
                voucher_amount: voucher_amount.toString(), // so that this page repopulated when we're redirected back from POLi
                toEmail: toEmail,
                toName: toName,
                note: note
              }}
              handleCreatePaymentSuccess={handleCreatePaymentSuccess}
              handlePaymentSuccess={() => {
                setToast({
                  message: `Payment succeeded 💳`,
                  open: true,
                  duration: 3000,
                  type: 'success'
                })
                refetch()
              }}
            />
            {!disabledCCButton && (
              <Button
                color='primary'
                data-testid='review-and-send-gift-button'
                id='buy-confirmation-button'
                fullWidth
                disabled={disabledCCButton}
                onClick={handleCCConfirm}
              >
                Confirm payment{' '}
                {formattedPrice(
                  roundUpDollarsAndCents(
                    Number(reviewPaymentDataFee?.payment_amount)
                  )
                )}
              </Button>
            )}
          </Grid2>
        )}

        <Grid2 container mt={3}>
          <Button
            color='primary'
            variant='outlined'
            data-testid='review-and-send-gift-button'
            fullWidth
            type='submit'
            disabled={loading || walletCredit < amount}
            onClick={handleBuyVoucher}
          >
            Send gift 🎁
          </Button>
        </Grid2>
      </Grid2>
    </DefaultLayout>
  )
}
